import React from 'react';
import { style } from 'typestyle';
import CheckIcon from 'mdi-react/CheckCircleIcon';
import { useTranslation } from 'react-i18next';
import * as styles from './cards.styles';
import type AgendaEntryParking from '../models/agenda-entry-parking';
import Column from '../../../core/components/column/column.component';
import { MainAxisAlignment, CrossAxisAlignment } from '../../../core/components/flex/flex.component';
import Row from '../../../core/components/row/row.component';
import { DayPart } from '../models/daypart-entry';

interface Props {
  readonly parking: AgendaEntryParking;
  readonly dayPart: DayPart;
  readonly isSelected: boolean;
  readonly onClick: (parking: AgendaEntryParking, DayPart: DayPart) => void;
}

const AgendaEntryParkingCard = ({ parking, dayPart, isSelected, onClick }: Props): React.JSX.Element => {
  const { t } = useTranslation();

  const freeSpaces = parking.parkingOccupations?.find((x) => x.dayPart === dayPart)?.freeSpaces;
  const isAvailable = freeSpaces !== undefined && freeSpaces > 0;

  const getSpacesText = (): string => {
    if (!isAvailable) {
      return t('places-available').replace('[x]', '0');
    }
    if (freeSpaces === 1) {
      return t('one-place-available');
    }

    return t('places-available').replace('[x]', freeSpaces.toString());
  };

  const handleClick = (): void => {
    if (!isAvailable) return;
    onClick(parking, dayPart);
  };

  return (
    <div
      className={style(styles.parkingInfoCard(isAvailable))}
      key={parking.name}
      onClick={handleClick}
      role="presentation"
      title={isAvailable ? undefined : (t('no-parking-spot-available') as string)}
    >
      <Row mainAxisAlignment={MainAxisAlignment.SpaceBetween} crossAxisAlignment={CrossAxisAlignment.Center}>
        <Column mainAxisAlignment={MainAxisAlignment.SpaceBetween}>
          <span className={style(styles.cardHeader())}>{parking.name}</span>
        </Column>
        <CheckIcon className={style(styles.checkIcon(isSelected && isAvailable))} />
      </Row>
      <Column>
        <span className={style(styles.cardBody(true))}>{getSpacesText()}</span>
      </Column>
    </div>
  );
};

export default AgendaEntryParkingCard;
